import api from './api';

/**
 * Home roll-up API. Backs the `/home` "all businesses" workspace view.
 * Server-composed + grant-filtered — the caller only ever sees the businesses
 * and sources they hold a grant on. See bizguard-backend GET /api/home/overview
 * and docs/MULTI_BUSINESS_HOME_PLAN.md.
 */

export type HomeAttentionKind = 'error' | 'stale_sync' | 'warning';

export interface HomeOverviewSource {
  id: string;
  name: string;
  sourceType: string;
  healthStatus: string;
  lastSync: string | null;
}

export interface HomeAttentionItem {
  sourceId: string;
  sourceName: string;
  kind: HomeAttentionKind;
  message: string;
}

export interface HomePulseSignal {
  severity: 'warn' | 'info';
  title: string;
  detail?: string;
  source?: string;
  at?: string;
}

export interface HomeBusiness {
  org: { id: string; name: string };
  sources: HomeOverviewSource[];
  attention: HomeAttentionItem[];
  /** Present only when the workspace tier includes Pulse (absent → hide column). */
  pulse?: HomePulseSignal[];
}

export type AiApp = 'chatgpt' | 'claude' | 'claude_code' | 'other';

/** One linked AI app, with the usage the home card reports on. */
export interface HomeAiConnection {
  app: AiApp;
  connectedAt: string | null;
  /** null = linked but never used. */
  lastUsedAt: string | null;
  calls30Days: number;
  /** 30 rolling daily buckets, oldest first — the row sparkline. */
  daily: number[];
}

export interface HomeOverview {
  workspace: { id: string; name: string; tier: string } | null;
  /** External AI apps with an approved MCP connection, most recently used first. */
  aiConnections: HomeAiConnection[];
  /** Calls made through the user's MCP connections in this workspace. */
  mcpCalls: { last30Days: number; previous30Days: number };
  businesses: HomeBusiness[];
  totals: { businesses: number; sources: number; attention: number };
}

export const homeApi = {
  getOverview: async (): Promise<HomeOverview> => {
    const { data } = await api.get<HomeOverview>('/api/home/overview');
    return data;
  },
};
